/**
 * Vollbild-Vorschau für Bilder, Videos und PDFs (presigned URLs).
 * Tastatur: Esc schließt, ←/→ blättert.
 */
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../../../components/ui';
import { formatFileSize, type MediaDocItem } from '../../../../lib/mediaDocuments';
import { formatMediaDate } from './shared';

interface Props {
  items: MediaDocItem[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
  /** Kategorie-Auswahl (nur mit users.write). */
  renderCategory?: (item: MediaDocItem) => React.ReactNode;
  onDelete?: (item: MediaDocItem) => void;
}

const BAR_BTN: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  height: 32, padding: '0 10px', borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.06)',
  color: '#f2f4f7', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', textDecoration: 'none',
  fontFamily: 'inherit', whiteSpace: 'nowrap',
};

export function MediaLightbox({ items, index, onIndex, onClose, renderCategory, onDelete }: Props) {
  const { t: tr, i18n } = useTranslation();
  const item = items[index];
  const closeRef = useRef<HTMLButtonElement>(null);
  const count = items.length;

  useEffect(() => {
    closeRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && count > 1) onIndex((index + 1) % count);
      else if (e.key === 'ArrowLeft' && count > 1) onIndex((index - 1 + count) % count);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, count, onIndex, onClose]);

  if (!item) return null;

  const metaLine = [formatFileSize(item.size), formatMediaDate(item.created_at, i18n.language)].filter(Boolean).join(' · ');

  const navBtn = (dir: 'left' | 'right'): React.CSSProperties => ({
    position: 'absolute', top: '50%', [dir]: 12, transform: 'translateY(-50%)',
    width: 42, height: 42, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)',
    background: 'rgba(16,24,40,0.55)', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2,
  });

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={item.name}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(8, 12, 20, 0.92)',
        display: 'flex', flexDirection: 'column',
        fontFamily: 'Outfit, sans-serif',
      }}
    >
      {/* Kopfleiste */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <div style={{ color: '#f2f4f7', fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>
            {item.name}
          </div>
          <div style={{ color: '#98a2b3', fontSize: 11.5, marginTop: 2 }}>
            {count > 1 ? `${index + 1} / ${count} · ` : ''}{metaLine}
          </div>
        </div>
        {renderCategory?.(item)}
        {item.url && (
          <a href={item.url} target="_blank" rel="noopener noreferrer" style={BAR_BTN}>
            {tr('pflege.media.open_new_tab')}
          </a>
        )}
        {item.download_url && (
          <a href={item.download_url} download={item.name} style={BAR_BTN}>
            <Icon.Download c="#f2f4f7" /> {tr('pflege.media.download')}
          </a>
        )}
        {onDelete && (
          <button type="button" onClick={() => onDelete(item)} style={{ ...BAR_BTN, color: '#fda29b', borderColor: 'rgba(253,162,155,0.35)' }}>
            <Icon.Trash c="#fda29b" /> {tr('common.delete')}
          </button>
        )}
        <button
          ref={closeRef}
          type="button"
          aria-label={tr('common.close')}
          title={tr('common.close')}
          onClick={onClose}
          style={{ ...BAR_BTN, width: 32, padding: 0, justifyContent: 'center' }}
        >
          <Icon.Close c="#f2f4f7" />
        </button>
      </div>

      {/* Inhalt */}
      <div
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      >
        {count > 1 && (
          <button type="button" aria-label={tr('pflege.media.previous')} style={navBtn('left')} onClick={() => onIndex((index - 1 + count) % count)}>
            <Icon.Chevron dir="left" c="#fff" />
          </button>
        )}

        {item.is_pdf && item.url ? (
          <iframe
            key={item.id}
            title={item.name}
            src={item.url}
            style={{ width: 'min(1100px, 100%)', height: '100%', border: 'none', borderRadius: 8, background: '#fff' }}
          />
        ) : item.is_video && item.url ? (
          <video
            key={item.id}
            src={item.url}
            poster={item.thumbnail_url ?? undefined}
            controls
            autoPlay
            playsInline
            style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8, background: '#000' }}
          />
        ) : item.url ? (
          <img
            key={item.id}
            src={item.url}
            alt={item.name}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 6, boxShadow: '0 12px 40px rgba(0,0,0,0.45)' }}
          />
        ) : (
          <div style={{ color: '#98a2b3', fontSize: 14 }}>{tr('pflege.media.no_preview')}</div>
        )}

        {count > 1 && (
          <button type="button" aria-label={tr('pflege.media.next')} style={navBtn('right')} onClick={() => onIndex((index + 1) % count)}>
            <Icon.Chevron dir="right" c="#fff" />
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
